import { JSX } from "react";
import { IconButton, Stack, Theme, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { ViewPage } from "../assets/icons";
import { formatNumber } from "../common/utils";
import { MeasurableValueDTO } from "../datasources/api-client/data-contracts";

type ColumnRenderAs = "text" | "date" | "measure" | "number";

export type ColumnDef = {
	field: string;
	headerName: string;
	hideable?: boolean;
	sortable?: boolean;
	flex?: number;
	renderAs: ColumnRenderAs;
	cellAlign?: "start" | "end";
	formatter?: (value: any, minDifgit: number, maxDigit: number) => string;
	headerMeasure?: string;
};

// Used internally -----------------------------------------------------------------------------------------------------

/**
 * Used internally to render the table cells content.
 * @param params
 * @param renderAs
 */
const renderCellContent = (
	params: GridRenderCellParams,
	renderAs: ColumnRenderAs,
	cellAlign?: "start" | "end",
	formatter?: (value: any, minDifgit: number, maxDigit: number) => string
): JSX.Element => {
	let content = "-";

	switch (renderAs) {
		case "text":
			if (!params.value) {
				return <>-</>;
			}
			content = params.value;
			break;
		case "date":
			if (!params.value) {
				return <></>;
			}
			content = dayjs(params.value).format("DD/MM/YYYY");
			break;
		case "measure":
			if (!params.value) {
				return (
					<Stack height="100%" justifyContent="center" alignItems={"end"}>
						<Typography variant="body2">0</Typography>
					</Stack>
				);
			}
			const dto = params.value as MeasurableValueDTO;
			content = formatNumber(dto.value, "decimal", 2, 2);
			break;
		case "number":
			if (!params.value) {
				return (
					<Stack height="100%" justifyContent="center" alignItems={"end"}>
						<Typography variant="body2">0</Typography>
					</Stack>
				);
			}

			content = formatter ? formatter(params.value, 0, 1) : formatNumber(params.value, "decimal", 2, 2);
			break;
	}

	return (
		<Stack height="100%" justifyContent="center" alignItems={"end"}>
			<Typography variant="body2">{content}</Typography>
		</Stack>
	);
};

/**
 * Used internally to render the table headers.
 *
 * @param data
 * @param def
 * @param t
 * @param color
 */
const renderHeaderContent = <TItem extends Record<string, any>>(
	data: { items: TItem[] | undefined } | undefined,
	def: ColumnDef,
	t: TFunction,
	color: string | string[] | undefined
): JSX.Element => {
	const label = t(`common:tables header/${def.headerName}`);

	let measure: string | undefined = def.headerMeasure;

	if (!measure && def.renderAs === "measure" && data?.items && data.items.length > 0) {
		const sample = data.items[0][def.field] as MeasurableValueDTO | undefined;
		measure = sample?.measure;
	}

	return (
		<Stack direction="row" spacing={1} alignItems="baseline">
			<Typography fontWeight={700} sx={{ color }} variant="body2">
				{label}
			</Typography>

			{measure && (
				<Typography sx={{ color }} variant="body2" color="text.secondary">
					{measure}
				</Typography>
			)}
		</Stack>
	);
};

// Exported ------------------------------------------------------------------------------------------------------------

/**
 * Maps data rows from a paginated response to a format suitable for use with the MUI DataGrid component.
 * If a rename mapping is provided, only fields contained in the mapping will be renamed.
 * The id field is always renamed to "id".
 *
 * @param data the source data
 * @param idField the name of the field that contains the row identifier
 * @param renameFields the map used to rename fields contained in data rows as something else: the mapping is defined
 *                     as a collection of entries like `<rename as>: <data.field>`
 */
export const mapDataRows = <TItem extends Record<string, any>>(
	data: { items: TItem[] | undefined } | undefined,
	idField: keyof TItem,
	renameFields?: Record<string, keyof TItem>
): (TItem & { id: TItem[keyof TItem] })[] => {
	if (!data?.items) {
		return [];
	}

	return data.items.map((item) => {
		// Create a copy of the source item.
		const row = { ...item, id: item[idField] } as TItem & { id: TItem[keyof TItem] };

		// If a mapping has been defined, apply it only to fields contained in the mapping.
		if (!renameFields) {
			return row;
		}

		for (const [target, source] of Object.entries(renameFields)) {
			(row as Record<string, any>)[target] = item[source];
			delete row[source as string];
		}

		return row;
	});
};

/**
 * Prepares both rows and columns to be used for rendering a data grid content and aspect.
 *
 * @param data the source data
 * @param t the translation function
 * @param theme the application theme
 * @param defs the columns definitions
 * @param onNavigate if defined, adds a column with a link to a desided location
 */
export const mapDataColumns = <TItem extends Record<string, any>>(
	data: { items: TItem[] | undefined } | undefined,
	t: TFunction,
	theme: Theme,
	defs: ColumnDef[],
	onNavigate?: (row: TItem) => void
): GridColDef[] => {
	const color = theme.palette.primary.contrastText;
	const columns: GridColDef[] = defs.map((d) => ({
		def: d,
		field: d.field,
		headerName: d.headerName,
		hideable: d.hideable ?? false,
		sortable: d.sortable ?? false,
		flex: d.flex ?? 1,
		renderHeader: () => renderHeaderContent(data, d, t, color),
		renderCell: (params: GridRenderCellParams) =>
			renderCellContent(params, d.renderAs, d.cellAlign || "start", d.formatter),
	})); // Render columns.

	if (!onNavigate) {
		return columns;
	}

	// Function `onNavigate` is defined, add the "actions" column.
	columns.push({
		field: "actions",
		headerName: t("common:actions"),
		hideable: true,
		sortable: false,
		width: 100,
		align: "center",
		renderHeader: () => (
			<Typography fontWeight={700} sx={{ color }} variant="body2">
				{t("common:actions")}
			</Typography>
		),
		renderCell: (params: GridRenderCellParams) => (
			<IconButton
				onClick={(e) => {
					e.stopPropagation();
					onNavigate(params.row);
				}}
			>
				<ViewPage fontSize="small" />
			</IconButton>
		),
	});

	return columns;
};
